import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Trash2, ShoppingBag, Check } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { items, remove, update, subtotal, deposit, total, clear } = useCart();
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", delivery_address: "", delivery_method: "pickup", start_date: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/prop-orders", {
        ...form,
        items: items.map((i) => ({ prop_id: i.prop_id, quantity: i.quantity, rental_days: i.rental_days })),
      });
      setDone(data); clear();
      toast.success("Order placed!");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Order failed");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="pt-32 pb-24 container mx-auto px-4 max-w-2xl text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-gradient-brand grid place-items-center mb-6">
          <Check className="w-8 h-8 text-white" />
        </div>
        <h1 className="font-display font-black text-4xl mb-3">Order received!</h1>
        <p className="text-zinc-600 mb-6">
          Order <span className="font-mono">#{done.id.slice(0, 8)}</span> total ₹{done.total.toLocaleString()}.
          We'll confirm via email/WhatsApp.
        </p>
        <button onClick={() => navigate("/props")} className="px-6 py-3 rounded-full bg-[#0A1128] text-white font-semibold">Continue browsing</button>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="pt-24">
      <section className="container mx-auto px-4 pt-12 pb-24">
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-8">Your Cart</h1>

        {items.length === 0 ? (
          <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-12 text-center">
            <ShoppingBag className="w-12 h-12 mx-auto text-zinc-400 mb-4" />
            <p className="text-zinc-600 mb-6">Your cart is empty.</p>
            <Link to="/props" className="px-6 py-3 rounded-full bg-[#0A1128] text-white font-semibold">Browse props</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-3">
              {items.map((it) => (
                <div key={it.prop_id} className="bg-white border border-zinc-200 rounded-2xl p-4 flex gap-4 items-center" data-testid={`cart-item-${it.prop_id}`}>
                  <img src={it.image} alt={it.name} className="w-20 h-20 object-cover rounded-lg" />
                  <div className="flex-1">
                    <div className="font-display font-bold">{it.name}</div>
                    <div className="text-xs text-zinc-500">₹{it.daily_rate}/day · ₹{it.deposit} deposit</div>
                    <div className="flex gap-3 mt-2">
                      <label className="text-xs flex items-center gap-1">Qty
                        <input type="number" min="1" value={it.quantity}
                          onChange={(e) => update(it.prop_id, { quantity: Math.max(1, +e.target.value) })}
                          className="w-14 px-2 py-1 border rounded" />
                      </label>
                      <label className="text-xs flex items-center gap-1">Days
                        <input type="number" min="1" value={it.rental_days}
                          onChange={(e) => update(it.prop_id, { rental_days: Math.max(1, +e.target.value) })}
                          className="w-14 px-2 py-1 border rounded" />
                      </label>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-bold">₹{(it.daily_rate * it.quantity * it.rental_days).toLocaleString()}</div>
                    <button onClick={() => remove(it.prop_id)} className="text-xs text-zinc-500 hover:text-red-500 flex items-center gap-1 mt-1">
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={submit} className="bg-zinc-50 border border-zinc-200 rounded-2xl p-6 h-fit space-y-3">
              <h3 className="font-display font-bold text-xl mb-2">Checkout</h3>
              <input required placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                data-testid="checkout-name" className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="checkout-email" className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
              <input required placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="checkout-phone" className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
              <input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                data-testid="checkout-date" className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
              <select value={form.delivery_method} onChange={(e) => setForm({ ...form, delivery_method: e.target.value })}
                className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm">
                <option value="pickup">Pickup from studio</option>
                <option value="delivery">Delivery</option>
              </select>
              {form.delivery_method === "delivery" && (
                <textarea placeholder="Delivery address" rows={2} value={form.delivery_address}
                  onChange={(e) => setForm({ ...form, delivery_address: e.target.value })}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
              )}
              <div className="border-t border-zinc-200 pt-3 mt-3 space-y-1 text-sm">
                <div className="flex justify-between"><span>Rental subtotal</span><b>₹{subtotal.toLocaleString()}</b></div>
                <div className="flex justify-between"><span>Deposit</span><b>₹{deposit.toLocaleString()}</b></div>
                <div className="flex justify-between font-display font-black text-lg pt-2 border-t border-zinc-200 mt-2">
                  <span>Total</span><span className="text-gradient">₹{total.toLocaleString()}</span>
                </div>
              </div>
              <button type="submit" disabled={busy} data-testid="checkout-submit"
                className="w-full mt-2 px-5 py-3 rounded-full bg-gradient-brand text-white font-bold disabled:opacity-50">
                {busy ? "Placing order…" : "Place Order"}
              </button>
              <p className="text-[10px] text-zinc-500 text-center">Pay later — our team will confirm payment options after order review.</p>
            </form>
          </div>
        )}
      </section>
    </div>
  );
}
